<template>
  <aside
    v-if="shouldShow"
    class="newbie-task-reminder"
    role="button"
    tabindex="0"
    @click="goToTaskCenter"
    @keyup.enter.self="goToTaskCenter"
    @keydown.space.self.prevent="goToTaskCenter"
  >
    <button
      class="newbie-task-reminder__close"
      type="button"
      aria-label="关闭新手任务提示"
      @click.stop="dismiss"
    >
      <i class="el-icon-close"></i>
    </button>

    <div class="newbie-task-reminder__content">
      <div class="newbie-task-reminder__icon">
        <i class="el-icon-message-solid"></i>
        <span class="newbie-task-reminder__pulse">
          <span class="newbie-task-reminder__pulse-ring"></span>
          <span class="newbie-task-reminder__pulse-dot"></span>
        </span>
      </div>
      <div class="newbie-task-reminder__copy">
        <div class="newbie-task-reminder__title">
          新手任务未完成
          <i class="el-icon-arrow-right"></i>
        </div>
        <div class="newbie-task-reminder__desc">完成即可解锁更多特权</div>
      </div>
    </div>

    <div class="newbie-task-reminder__progress">
      <div class="newbie-task-reminder__bar">
        <div
          class="newbie-task-reminder__bar-fill"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
      <span class="newbie-task-reminder__count">
        {{ completedCount }}/{{ tasks.length }}
      </span>
    </div>
  </aside>
</template>

<script>
import { getTaskCenter } from "@/aips/task";

export default {
  name: "NewbieTaskReminder",
  data() {
    return {
      tasks: [],
      loaded: false,
      dismissed: false,
      requestSeq: 0,
    };
  },
  computed: {
    completedCount() {
      return this.tasks.filter((task) => task.periodCompleted).length;
    },
    allCompleted() {
      return (
        this.tasks.length > 0 && this.completedCount === this.tasks.length
      );
    },
    progressPercent() {
      if (!this.tasks.length) return 0;
      return (this.completedCount / this.tasks.length) * 100;
    },
    shouldShow() {
      return (
        this.loaded &&
        !this.dismissed &&
        this.tasks.length > 0 &&
        !this.allCompleted
      );
    },
  },
  watch: {
    shouldShow: {
      immediate: true,
      handler(visible) {
        this.$emit("visibility-change", visible);
      },
    },
    "$route.fullPath"() {
      this.loadTasks();
    },
  },
  created() {
    this.loadTasks();
  },
  beforeDestroy() {
    this.$emit("visibility-change", false);
  },
  methods: {
    async loadTasks() {
      const seq = ++this.requestSeq;
      try {
        const res = await getTaskCenter();
        if (seq !== this.requestSeq) return;
        const groups =
          res && res.code === 200 && res.data && Array.isArray(res.data.groups)
            ? res.data.groups
            : [];
        const newbieGroup = groups.find(
          (group) => group.groupCode === "NEWBIE",
        );
        this.tasks =
          newbieGroup && Array.isArray(newbieGroup.tasks)
            ? newbieGroup.tasks
            : [];
      } catch (error) {
        if (seq !== this.requestSeq) return;
        this.tasks = [];
      } finally {
        if (seq === this.requestSeq) this.loaded = true;
      }
    },
    dismiss() {
      this.dismissed = true;
    },
    goToTaskCenter() {
      if (this.$route.path === "/personalinfo") {
        const taskCenter = document.getElementById("uc-task-center");
        if (taskCenter) {
          taskCenter.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }

      this.$router
        .push({ path: "/personalinfo", query: { focus: "tasks" } })
        .catch(() => {});
    },
  },
};
</script>

<style lang="less" scoped>
@keyframes newbie-task-ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.newbie-task-reminder {
  position: fixed;
  right: 32px;
  bottom: 32px;
  z-index: 99;
  width: 260px;
  padding: 16px;
  box-sizing: border-box;
  border: 1px solid #ebedf1;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  color: #0d0e0f;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.16);
    transform: translateY(-4px);
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #a2a9b6;
    cursor: pointer;

    &:hover {
      background: #f4f6f9;
      color: #0d0e0f;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-right: 18px;
  }

  &__icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #fef2f2;
    color: #ef4444;

    > i {
      font-size: 16px;
    }
  }

  &__pulse {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    width: 10px;
    height: 10px;
  }

  &__pulse-ring,
  &__pulse-dot {
    display: inline-flex;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #ef4444;
  }

  &__pulse-ring {
    position: absolute;
    opacity: 0.75;
    animation: newbie-task-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  &__pulse-dot {
    position: relative;
    background: #dc2626;
  }

  &__copy {
    min-width: 0;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;

    i {
      flex-shrink: 0;
      color: #a2a9b6;
    }
  }

  &__desc {
    margin-top: 2px;
    color: #87909f;
    font-size: 11px;
    white-space: nowrap;
  }

  &__progress {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 0 4px;
  }

  &__bar {
    height: 6px;
    flex: 1;
    overflow: hidden;
    border-radius: 9999px;
    background: #ebedf1;
  }

  &__bar-fill {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(to right, #3b82f6, #60a5fa);
    transition: width 0.5s;
  }

  &__count {
    color: #3b82f6;
    font-size: 12px;
    font-weight: 700;
  }
}

@media (max-width: 639px) {
  .newbie-task-reminder {
    right: 16px;
    bottom: 16px;
    width: min(260px, calc(100vw - 32px));
  }
}
</style>
